import React, { useEffect, useState } from "react";
import { Box, IconButton, Typography, Menu, MenuItem, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight, Dashboard, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import userIcon from "../../../static/media/icon/user.svg";
import briefcaseIcon from "../../../static/media/icon/briefcase.svg";

const getCSRFTokenFromCookie = () => {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return "";
};

const fetchCsrfToken = async () => {
  const fromCookie = getCSRFTokenFromCookie();
  if (fromCookie) return fromCookie;

  try {
    const r = await fetch("/sistema/get-csrf-token/", { method: "GET", credentials: "include" });
    const d = await r.json().catch(() => ({}));
    return d?.csrfToken || d?.csrf || d?.token || "";
  } catch {
    return "";
  }
};

const TopBar = ({ open, setOpen }) => {
  const [email, setEmail] = useState(localStorage.getItem("user_email") || "");
  const [role, setRole] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [empresa, setEmpresa] = useState("");
  const [ambiente, setAmbiente] = useState("2");

  const navigate = useNavigate();

  useEffect(() => {
    const s = localStorage.getItem("is_superuser") === "true";
    const a = localStorage.getItem("is_admin") === "true";
    const v = localStorage.getItem("is_vendedor") === "true";

    if (s) {
      setRole("SUPERUSUARIO");
      setIsAdmin(true);
    } else if (a) {
      setRole("ADMINISTRADOR");
      setIsAdmin(true);
    } else if (v) {
      setRole("VENDEDOR");
      setIsAdmin(false);
    } else {
      setRole("");
      setIsAdmin(false);
    }

    const fetchSistema = async () => {
      try {
        const res = await fetch("/facturas/empresa/info/", { credentials: "include" });
        if (!res.ok) return;

        const data = await res.json().catch(() => ({}));
        const nc = String(data?.nombre_comercial || "").trim();
        const rs = String(data?.razon_social || "").trim();
        const em = String(data?.empresa || "").trim();

        setEmpresa(nc || em || rs || "");
        setAmbiente(String(data?.ambiente || "2").trim() === "1" ? "1" : "2");
      } catch {}
    };

    fetchSistema();

    if (!email) {
      const fetchUser = async () => {
        try {
          const csrf = await fetchCsrfToken();
          const res = await fetch("/sistema/get_user_info/", {
            method: "GET",
            credentials: "include",
            headers: csrf ? { "X-CSRFToken": csrf } : {},
          });

          if (!res.ok) return;

          const data = await res.json().catch(() => ({}));
          if (data?.email) {
            localStorage.setItem("user_email", data.email);
            setEmail(data.email);
          }

          const ss = !!data?.is_superuser;
          const aa = !!data?.is_admin;
          const vv = !!data?.is_vendedor;

          if (ss) {
            setRole("SUPERUSUARIO");
            setIsAdmin(true);
          } else if (aa) {
            setRole("ADMINISTRADOR");
            setIsAdmin(true);
          } else if (vv) {
            setRole("VENDEDOR");
            setIsAdmin(false);
          }

          localStorage.setItem("is_superuser", String(ss));
          localStorage.setItem("is_admin", String(aa));
          localStorage.setItem("is_vendedor", String(vv));
        } catch {}
      };

      fetchUser();
    }
  }, [email]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      const csrf = await fetchCsrfToken();
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRFToken": csrf } : {},
      });
    } catch {}

    localStorage.removeItem("user_email");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_vendedor");
    localStorage.removeItem("is_logged_in");
    navigate("/");
  };

  const dotColor = ambiente === "2" ? "#4CAF50" : "#F44336";

  return (
    <Box
      sx={{
        width: "95%",
        mx: "auto",
        mt: 1,
        display: "flex",
        alignItems: "center",
        background: "#fafafa",
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton onClick={() => setOpen(!open)}>
          {open ? (
            <ChevronLeft sx={{ fontSize: 30, color: "black" }} />
          ) : (
            <ChevronRight sx={{ fontSize: 30, color: "black" }} />
          )}
        </IconButton>

        <IconButton onClick={() => navigate("/admin-panel")}>
          <Dashboard sx={{ fontSize: 30, color: "black" }} />
        </IconButton>

        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, ml: 1, color: "#444" }}>
          {role}
        </Typography>
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          textAlign: "center",
          maxWidth: "55%",
          px: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.05rem",
            fontWeight: 700,
            color: "#1565C0",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {empresa || "—"}
        </Typography>
      </Box>

      <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 2 }}>
        {isAdmin && (
          <Tooltip title="Mi Compañía" arrow>
            <Box
              onClick={() => navigate("/admin-panel/empresa")}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                p: "4px 6px",
                borderRadius: "6px",
              }}
            >
              <img
                src={briefcaseIcon}
                alt="Compañía"
                style={{
                  width: 30,
                  height: 30,
                  objectFit: "contain",
                }}
              />
            </Box>
          </Tooltip>
        )}

        <Box
          onClick={handleMenuOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            userSelect: "none",
            p: "4px 8px",
            borderRadius: "6px",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <img
              src={userIcon}
              alt="Usuario"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: dotColor,
                border: "2px solid white",
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 500,
              color: "#1565C0",
              letterSpacing: 0.3,
            }}
          >
            {email || "—"}
          </Typography>

          <ExpandMore sx={{ color: "#1565C0", fontSize: 22 }} />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 10px rgba(0,0,0,0.15)",
          },
        }}
      >
        <MenuItem onClick={handleMenuClose}>Mi perfil</MenuItem>
        <MenuItem onClick={handleMenuClose}>Mi cuenta</MenuItem>
        <MenuItem onClick={handleLogout}>Cerrar Sesión</MenuItem>
      </Menu>
    </Box>
  );
};

export default TopBar;
